import { useTranslation } from 'react-i18next'
import { Tooltip } from 'antd'
import { AdvertiserReportItem } from '../../../../../app/api'
import { IconVerified } from '../../../../../shared/components/icons/IconVerified'
import CountryList from '../../../../../shared/ui/cells/CountriesCell'
import { cellClass, Table, TableCell } from '../../../../../shared/ui/table/Table'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../../../app/store/hooks/Redux'
import { AdvertiserReportFilterInterface } from '../../../model/types/AdvertiserReportFilter.interface'
import { selectAdvertiserReportFilters } from '../../../model/paidResultsSlice'
import { adOverviewLink } from './ReportLinks'

interface IAdvertisersTabProps {
  advertisers: AdvertiserReportItem['advertisers']
}

export const AdvertisersTab = ({ advertisers }: IAdvertisersTabProps) => {
  const { t } = useTranslation()
  const [expandedAffiliates, setExpandedAffiliates] = useState<number[]>([])
  const [countRows, setCountRows] = useState<number>(5)
  const filters = useAppSelector<AdvertiserReportFilterInterface>(selectAdvertiserReportFilters)
  const dispatch = useAppDispatch()

  const toggleAffiliates = (index: number) =>
    setExpandedAffiliates((prevState) =>
      prevState.includes(index)
        ? prevState.filter((rowIndex) => rowIndex !== index)
        : [...prevState, index],
    )

  const HEADERS = [
    { label: t('table.columns.advertiser') },
    { label: t('table.columns.affiliateId'), width: '240px' },
    { label: t('table.columns.ads'), width: '100px' },
    { label: t('table.columns.searchEngine'), width: '172px' },
    { label: t('table.columns.countries'), width: '172px' },
    { label: t('table.columns.lastFound'), width: '172px' },
    { label: '' },
  ]

  return (
    <Table headers={HEADERS} isEmpty={!advertisers.length}>
      {advertisers.slice(0, countRows + 1).map((advertiser, index) => {
        const affiliates = advertiser.affiliates ?? []
        const isExpanded = expandedAffiliates.includes(index)
        const visibleAffiliates = isExpanded ? affiliates : affiliates.slice(0, 1)

        return (
          <tr key={index} className={'relative'}>
            <TableCell className={cellClass}>
              <div>
                <p>
                  {advertiser.name}{' '}
                  {advertiser.verified && (
                    <Tooltip
                      title={t('pages.paidResults.tooltipVerifiedByFallback', {
                        name: advertiser.verified_by,
                      })}
                    >
                      <span>
                        <IconVerified className='ml-1 inline w-[16px]' color='#00BD7B' />
                      </span>
                    </Tooltip>
                  )}
                </p>
                <p className={'text-[13px] leading-[18px]'}>
                  {t('pages.paidResults.location')}: {advertiser.location}
                </p>
                <p className={'text-[13px] leading-[18px]'}>
                  {t('pages.paidResults.verifiedBy')}: {advertiser.verified_by}
                </p>
              </div>
            </TableCell>
            <TableCell>
              {affiliates.length ? (
                <div className={'flex flex-col gap-[12px]'}>
                  <div className={'flex flex-col gap-[8px]'}>
                    {visibleAffiliates.map((affiliate, affiliateIndex) => (
                      <div key={affiliateIndex} className={'flex flex-col gap-[4px]'}>
                        <div>{affiliate.affiliate_id}</div>
                        {!!affiliate.publisher && (
                          <Tooltip
                            className={'block max-w-[130px] truncate text-[10px] leading-none'}
                            title={affiliate.publisher}
                          >
                            {affiliate.publisher}
                          </Tooltip>
                        )}
                      </div>
                    ))}
                  </div>
                  {affiliates.length > 1 && (
                    <span
                      className={
                        'w-fit cursor-pointer select-none border-b border-dotted text-[13px] leading-[13px] text-focus'
                      }
                      onClick={() => toggleAffiliates(index)}
                    >
                      {isExpanded
                        ? t('pages.paidResults.showLess')
                        : t('pages.paidResults.showMore', {
                            count: affiliates.length - 1,
                          })}
                    </span>
                  )}
                </div>
              ) : (
                '—'
              )}
            </TableCell>
            <TableCell>{advertiser.ads_count}</TableCell>
            <TableCell>{advertiser.search_engine}</TableCell>
            <TableCell>
              <CountryList countries={advertiser.countries} max={3} />
            </TableCell>
            <TableCell>{advertiser.last_found}</TableCell>
            <TableCell>
              <Link
                {...adOverviewLink(
                  filters,
                  { advertiserName: advertiser.advertiser_id as string },
                  dispatch,
                )}
              >
                <button className={'text-focus'}>{t('pages.paidResults.showAllAds')}</button>
              </Link>
            </TableCell>
            {index === countRows && (
              <div
                className={
                  'absolute top-0 left-0 flex h-full w-full items-center justify-center bg-white/[.9]'
                }
              >
                <button
                  className={'text-[14px] font-semibold text-default'}
                  onClick={() => setCountRows(advertisers.length)}
                >
                  {t('pages.paidResults.showMore', {
                    count: advertisers.length - countRows,
                  })}
                </button>
              </div>
            )}
          </tr>
        )
      })}
    </Table>
  )
}
